import { Clipboard } from '@angular/cdk/clipboard';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { PlusAuthenticationService } from '@karve.it/core';
import { RawUser } from '@karve.it/interfaces/auth';
import { SubSink } from 'subsink';

import { environment } from '../../../environments/environment';

import { AvailableZoneFragment } from '../../../generated/graphql.generated';
import { PlusApollo } from '../../core/auth/graphql.module';
import { AvailableZonesService } from '../../services/available-zones.service';
import { FreyaNotificationsService } from '../../services/freya-notifications.service';

import {
    MCP_TOKENS_QUERY,
    CREATE_MCP_TOKEN_MUTATION,
    REVOKE_MCP_TOKEN_MUTATION,
} from './mcp-connections.graphql';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface McpToken {
    id: string;
    createdAt: number;
    expiration?: number;
    isValid?: boolean;
    used?: number;
    lastUsed?: number;
    /** Metadata scalar — arrives as a parsed object (or JSON string) from the GraphQL Metadata scalar */
    metadata?: any;
}

export interface McpTokenMetadata {
    name: string;
    provider: string;
    scope: string;
    zoneId: string;
    roleId: string;
}

export type McpTokenScope = 'reports' | 'read' | 'write' | 'admin';

export interface McpProvider {
    id: string;
    label: string;
    icon: string;
    description: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const PROVIDERS: McpProvider[] = [
    {
        id: 'claude',
        label: 'Claude',
        icon: 'pi pi-desktop',
        description: 'Claude Desktop or claude.ai — OAuth 2.0 connection (no token needed)',
    },
    {
        id: 'hey-patrick',
        label: 'Hey Patrick',
        icon: 'pi pi-envelope',
        description: 'Hey Patrick AI assistant — email sajjad@heypatrick.ai to get set up',
    },
    {
        id: 'other',
        label: 'Other',
        icon: 'pi pi-cog',
        description: 'Any other MCP-compatible AI assistant',
    },
];

const SCOPES: { value: McpTokenScope; label: string; description: string }[] = [
    { value: 'reports', label: 'Reports', description: 'Run and view reports only — no job data' },
    { value: 'read', label: 'Read', description: 'Read jobs, events, and reports — no writes' },
    { value: 'write', label: 'Write', description: 'Read access plus save and delete reports' },
    { value: 'admin', label: 'Admin', description: 'Reserved scope — currently equivalent to Write. For future admin-level tools.' },
];

const MCP_BASE_PATH = '/api/mcp';

@Component({
    selector: 'app-mcp-connections',
    templateUrl: './mcp-connections.component.html',
    styleUrls: ['./mcp-connections.component.scss'],
})
export class McpConnectionsComponent implements OnInit, OnDestroy {

    @Input() user: RawUser;

    subs = new SubSink();

    // ── Wizard state ──────────────────────────────────────────────────────────

    showWizard = false;
    /**
     * 1 = provider + name
     * 2 = zone + role
     * 3 = scope
     * 4 = install instructions
     */
    wizardStep = 1;

    selectedProvider: McpProvider | null = null;
    tokenName = '';
    selectedRoleId = '';
    selectedScope: McpTokenScope = 'read';

    // ── Data ──────────────────────────────────────────────────────────────────

    tokens: McpToken[] = [];
    tokensLoading = false;

    currentZoneName = '';
    currentZoneRoles: Array<{ id: string; name: string }> = [];
    private availableZones: AvailableZoneFragment[] = [];

    /** Raw token string (id:secret) — only available immediately after creation */
    newTokenSecret: string | null = null;
    newTokenId: string | null = null;
    creatingToken = false;

    // ── Confirmation ─────────────────────────────────────────────────────────

    showRevokeConfirm = false;
    tokenToRevoke: McpToken | null = null;
    revoking = false;

    // ── Exposed constants ─────────────────────────────────────────────────────

    readonly providers = PROVIDERS;
    readonly scopes = SCOPES;

    constructor(
        private apollo: PlusApollo,
        private plusAuth: PlusAuthenticationService,
        private availableZonesService: AvailableZonesService,
        private clipboard: Clipboard,
        private localNotify: FreyaNotificationsService,
    ) {}

    ngOnInit(): void {
        this.loadTokens();

        this.subs.sink = this.availableZonesService.availableZones.subscribe((zones) => {
            if (zones) {
                this.availableZones = zones;
                this.resolveCurrentZone();
            }
        });

        // Also seed from whatever value is already in the BehaviorSubject right now
        // (the subscribe above only fires on future emissions if the subject already has a value)
        const existing = this.availableZonesService.availableZones.value;
        if (existing) {
            this.availableZones = existing;
            this.resolveCurrentZone();
        }
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
    }

    // ── Token loading ─────────────────────────────────────────────────────────

    loadTokens(): void {
        this.tokensLoading = true;
        this.subs.sink = this.apollo.query<{ mcpTokens: McpToken[] }>({
            query: MCP_TOKENS_QUERY,
            fetchPolicy: 'network-only',
        }).subscribe({
            next: (res) => {
                this.tokens = res.data?.mcpTokens ?? [];
                this.tokensLoading = false;
            },
            error: (err) => {
                this.localNotify.apolloError('Failed to load MCP connections', err);
                this.tokensLoading = false;
            },
        });
    }

    // ── Wizard ────────────────────────────────────────────────────────────────

    openWizard(): void {
        this.resetWizard();
        this.resolveCurrentZone();
        this.showWizard = true;
    }

    closeWizard(): void {
        this.showWizard = false;
        this.resetWizard();
    }

    resetWizard(): void {
        this.wizardStep = 1;
        this.selectedProvider = null;
        this.tokenName = '';
        this.selectedRoleId = this.plusAuth.contextedRoleId ?? '';
        this.selectedScope = 'read';
        this.newTokenSecret = null;
        this.newTokenId = null;
        this.creatingToken = false;
    }

    // Step 1 → 2: select provider, default name
    selectProvider(provider: McpProvider): void {
        this.selectedProvider = provider;
        if (!this.tokenName) {
            // Use a friendlier default name than the raw provider label
            this.tokenName = 'Claude';
        }
    }

    canAdvanceStep1(): boolean {
        return !!(this.selectedProvider && this.tokenName.trim());
    }

    goToStep2(): void {
        if (this.canAdvanceStep1()) {
            if (this.selectedProvider?.id === 'claude') {
                this.wizardStep = 4;
                return;
            }
            // Requirement: MCP tokens do not persist zone or role context,
            // so skip the removed selection step and go directly to step 3.
            this.wizardStep = 3;
        }
    }

    /** Steps shown in the indicator — fewer for OAuth providers. */
    wizardSteps(): number[] {
        if (this.selectedProvider?.id === 'claude') {
            return [1, 4];
        }
        // Step 2 (role/zone selection) removed — MCP tokens no longer carry
        // a zoneId/roleId. The zone is supplied per-request via the tool's
        // zoneId argument and validated server-side.
        return [1, 3, 4];
    }

    // Step 2: role selection — removed. MCP tokens no longer store a role;
    // permissions are combined across all the user's roles per request.
    get multipleRoles(): boolean {
        return false;
    }

    canAdvanceStep2(): boolean {
        return true;
    }

    goToStep3(): void {
        this.wizardStep = 3;
    }

    // Step 3 → create token
    canCreate(): boolean {
        return !!(this.selectedScope && this.tokenName?.trim());
    }

    createToken(): void {
        if (!this.canCreate()) { return; }
        this.creatingToken = true;

        this.subs.sink = this.apollo.mutate<{ createMcpToken: McpToken & { token: string } }>({
            mutation: CREATE_MCP_TOKEN_MUTATION,
            variables: {
                name: this.tokenName.trim(),
                provider: this.selectedProvider?.id ?? 'other',
                scope: this.selectedScope,
            },
        }).subscribe({
            next: (res) => {
                const created = res.data?.createMcpToken;
                if (created) {
                    this.newTokenSecret = created.token ?? null;
                    this.newTokenId = created.id;
                    this.wizardStep = 4;
                    this.loadTokens();
                }
                this.creatingToken = false;
            },
            error: (err) => {
                this.localNotify.apolloError('Failed to create MCP token', err);
                this.creatingToken = false;
            },
        });
    }

    // ── Token URL helpers ─────────────────────────────────────────────────────

    getMcpUrl(): string {
        // Claude Desktop uses OAuth — no token in the URL.
        if (this.selectedProvider?.id === 'claude') {
            return `${window.location.origin}${MCP_BASE_PATH}`;
        }
        if (!this.newTokenSecret) { return ''; }
        const profile = this.selectedScope === 'reports' ? '/reporting' : '';
        return `${window.location.origin}${MCP_BASE_PATH}${profile}?token=${this.newTokenSecret}`;
    }

    copyMcpUrl(): void {
        const url = this.getMcpUrl();
        if (url) {
            this.clipboard.copy(url);
            this.localNotify.addToast.next({ severity: 'success', summary: 'MCP URL copied' });
        }
    }

    /** Numbered install steps rendered in the template. */
    getInstallSteps(): Array<{ heading: string; body: string; code?: string }> {
        const url = this.getMcpUrl();
        const provider = this.selectedProvider?.id ?? 'other';

        if (provider === 'claude') {
            const mcpUrl = url.replace(/\?token=.*/, '');
            const clientId = environment.faClientId || '';
            return [
                {
                    heading: 'Open Claude config',
                    body: 'For Claude Desktop: go to Settings → Developer → Edit Config. ' +
                        'This opens the claude_desktop_config.json file. ' +
                        'For claude.ai: go to Settings → Integrations → Add integration.',
                },
                {
                    heading: 'Add MoveRight MCP server',
                    body: 'Add the MoveRight server with the OAuth client ID and secret. ' +
                        'Claude will discover the OAuth endpoints automatically when it connects:',
                    code: JSON.stringify({
                        mcpServers: {
                            moveright: {
                                type: 'url',
                                url: mcpUrl,
                                clientId,
                                clientSecret: environment.faClientSecret || '',
                            },
                        },
                    }, null, 2),
                },
                {
                    heading: 'Restart Claude',
                    body: 'Save the config and restart Claude Desktop, or save the integration in claude.ai. ' +
                        'When you start a new chat, Claude will open a browser window to authorize with MoveRight.',
                },
                {
                    heading: 'Authorize and start chatting',
                    body: 'Log in with your MoveRight credentials in the browser window that opens. ' +
                        'After authorization, Claude will have access to your MoveRight tools. ' +
                        'Look for the hammer icon (🔨) in the chat input to confirm tools are active.',
                },
            ];
        }

        if (provider === 'hey-patrick') {
            return [
                {
                    heading: 'Copy your MCP URL',
                    body: 'Copy the connection URL below — you\'ll need to include it in your setup email:',
                    code: url,
                },
                {
                    heading: 'Email Hey Patrick support',
                    body: 'Send an email to sajjad@heypatrick.ai with the subject "MoveRight MCP Setup". ' +
                        'Include your account name, the domain you use to log in to MoveRight, ' +
                        'and paste your MCP URL from the step above. The Hey Patrick team will configure your account.',
                },
                {
                    heading: 'Wait for confirmation',
                    body: 'Once Hey Patrick confirms your integration is active, open Hey Patrick and ' +
                        'verify that MoveRight tools are available before starting your first session.',
                },
            ];
        }

        // Generic / other
        return [
            {
                heading: 'Connect via SSE transport',
                body: 'Configure your MCP client to connect using Server-Sent Events (SSE). Use the URL below as the server endpoint:',
                code: url,
            },
            {
                heading: 'Authentication',
                    body: 'The token is embedded in the URL — no extra headers or API keys are needed. ' +
                        'Keep this URL private; anyone with it can access MoveRight with your chosen permissions.',
            },
            {
                heading: 'Working with zones',
                    body: 'Every tool call requires a zoneId. Ask the AI to run the ' +
                        'available_zones tool first to see your zones, then pass the ' +
                        'zoneId to subsequent tool calls (e.g. find_job, run_report).',
            },
        ];
    }

    copyInstallConfig(): void {
        const steps = this.getInstallSteps();
        const codeStep = steps.find((s) => s.code);
        if (codeStep?.code) {
            this.clipboard.copy(codeStep.code);
            this.localNotify.addToast.next({ severity: 'success', summary: 'Config copied' });
        }
    }

    // ── Revoke ────────────────────────────────────────────────────────────────

    promptRevoke(token: McpToken): void {
        this.tokenToRevoke = token;
        this.showRevokeConfirm = true;
    }

    cancelRevoke(): void {
        this.showRevokeConfirm = false;
        this.tokenToRevoke = null;
    }

    confirmRevoke(): void {
        if (!this.tokenToRevoke) { return; }
        this.revoking = true;

        this.subs.sink = this.apollo.mutate<{ revokeMcpToken: boolean }>({
            mutation: REVOKE_MCP_TOKEN_MUTATION,
            variables: { tokenId: this.tokenToRevoke.id },
        }).subscribe({
            next: () => {
                this.localNotify.addToast.next({ severity: 'success', summary: 'MCP connection revoked' });
                this.showRevokeConfirm = false;
                this.tokenToRevoke = null;
                this.revoking = false;
                this.loadTokens();
            },
            error: (err) => {
                this.localNotify.apolloError('Failed to revoke token', err);
                this.revoking = false;
            },
        });
    }

    // ── Helpers ───────────────────────────────────────────────────────────────

    getTokenMeta(token: McpToken): McpTokenMetadata | null {
        try {
            if (!token.metadata) { return null; }
            // The Metadata GraphQL scalar is returned already parsed as an object.
            // Guard against the edge case where it arrives as a JSON string anyway.
            const raw: any = token.metadata;
            return (typeof raw === 'string' ? JSON.parse(raw) : raw) as McpTokenMetadata;
        } catch {
            return null;
        }
    }

    getTokenName(token: McpToken): string {
        return this.getTokenMeta(token)?.name ?? token.id;
    }

    getTokenProvider(token: McpToken): string {
        const p = this.getTokenMeta(token)?.provider ?? 'other';
        return PROVIDERS.find((x) => x.id === p)?.label ?? p;
    }

    getTokenScope(token: McpToken): string {
        const s = this.getTokenMeta(token)?.scope ?? '';
        return SCOPES.find((x) => x.value === s)?.label ?? s;
    }

    private resolveCurrentZone(): void {
        const zoneId = this.plusAuth.contextedZoneId;
        if (!zoneId) { return; }
        const match = this.availableZones.find((z) => z.id === zoneId);
        this.currentZoneName = match?.name ?? zoneId;
        this.currentZoneRoles = match?.contextableRoles?.map((r) => ({ id: r.id, name: r.name })) ?? [];
        // Keep selectedRoleId in sync if it's not set yet or no longer valid
        const valid = this.currentZoneRoles.some((r) => r.id === this.selectedRoleId);
        if (!valid) {
            this.selectedRoleId = this.plusAuth.contextedRoleId ?? this.currentZoneRoles[0]?.id ?? '';
        }
    }

    formatDate(seconds: number | undefined): string {
        if (!seconds) { return '—'; }
        return new Date(seconds * 1000).toLocaleDateString();
    }

    isAdminScope(): boolean {
        return this.selectedScope === 'admin';
    }

    /** Step label shown in the dialog header */
    get wizardStepLabel(): string {
        switch (this.wizardStep) {
            case 1: return 'Choose assistant';
            case 2: return 'Zone & role';
            case 3: return 'Permissions';
            case 4: return 'Install';
            default: return '';
        }
    }
}
